/**
 * Leave the sign-in flow with a REAL navigation, never a client-side one.
 *
 * This is the last piece of the iOS 26 top-edge blur (ui/top-edge-sample.tsx
 * has the rest). WebKit decides the colour it paints behind the status bar of
 * an installed web app by sampling the page's top edge, and
 * `Page::updateFixedContainerEdges` samples the TOP edge only while the
 * document is loading or until the reader's first tap or scroll. After that
 * the colour is frozen for the life of the page load, and it survives only
 * while the element it was read from is still in the DOM: the moment that
 * element goes, the edge is dropped and the system falls back to its glass
 * blur — with no way to sample again.
 *
 * Signing in is a tap, and a client-side route change then unmounts the
 * sign-in frame (AuthPageShell) — exactly that sequence. Whatever was sampled
 * on the sign-in screen is first frozen and then discarded, and every screen
 * after it blurs until the app is relaunched. The persistent root strip
 * (`<TopEdgeSample persistent />` in App.tsx) keeps an element alive across
 * the route change; a page load is what lets WebKit sample it fresh, before
 * anyone has touched the new document.
 *
 * So the app is entered with `location.replace` — `replace`, not `assign`, for
 * the same reason the router calls used `replace: true`: Back must not return
 * to a sign-in form. Sign-out leaves the same way, in reverse
 * (AuthContext.logout → `window.location.assign('/login')`).
 *
 * Every completed sign-in goes through here: the password form and a passkey
 * (checkSession's remembered destination, in AuthContext), the 2FA screen
 * (App.tsx onVerified), and AppContent's `/login` → `/projects` and `/verify`
 * → `/projects` redirects. The SSO round trip comes back from the server as a
 * full load already. Keep any return-to / destination logic where it is and
 * hand the computed path in.
 *
 * One-shot per document: the first door reached wins. More than one fires on
 * the same sign-in (checkSession lands the remembered destination, then
 * AppContent's effect asks for `/projects`), and with a page load pending a
 * second `replace` would override the first destination rather than be
 * absorbed by the router the way a second `setLocation` was.
 */
let leaving = false;

export function landAfterSignIn(path: string): void {
  if (leaving) return;
  leaving = true;
  window.location.replace(path);
}
